/*
 * File: TextInput.tsx
 * Author: Austin Robinson
 * Date Created: 10/18/2023
 * Copyright (c) DrivingDoc 2023
 */
import TextField, { StandardTextFieldProps, TextFieldProps, TextFieldVariants } from '@mui/material/TextField';
import { Controller, useFormContext } from 'react-hook-form';

interface PropTypes extends StandardTextFieldProps {
  name: string;
  label: string;
}

export default function TextInput({ name, label, ...textFieldProps }: PropTypes) {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          name={name}
          label={label}
          type="text"
          variant="standard"
          fullWidth
          InputLabelProps={{ shrink: true }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          ref={field.ref}
          value={field.value ?? ''}
          onBlur={field.onBlur}
          onChange={(event) => {
            field.onChange(event.target.value);
          }}
          {...textFieldProps}
        />
      )}
    />
  );
}

// Define PropTypes that dynamically adjusts based on the variant type
type TextInputV2PropTypes<Variant extends TextFieldVariants = 'standard'> = Omit<TextFieldProps<Variant>, 'variant'> & {
  name: string;
  label: string;
  variant?: Variant;
};

export function TextInputV2<Variant extends TextFieldVariants = 'standard'>({
  name,
  label,
  variant = 'standard' as Variant,
  ...textFieldProps
}: TextInputV2PropTypes<Variant>) {
  const { control } = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField
          name={name}
          label={label}
          type="text"
          variant={variant}
          fullWidth
          InputLabelProps={{ shrink: true }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          ref={field.ref}
          value={field.value ?? ''}
          onBlur={field.onBlur}
          onChange={(event) => {
            field.onChange(event.target.value);
          }}
          {...textFieldProps}
        />
      )}
    />
  );
}
